import React from "react";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { FaHeart, FaArrowRight, FaCrown, FaStar } from "react-icons/fa";
import Swal from "sweetalert2";
import ProfileCard from "../ProfileCard/ProfileCard";
import LockedCard from "../ProfileCard/LockedCard";
import "./ResultsSection.css";
import { BASE_API } from "../../../Api/Api";

function ResultsSection({
  data,           // raw data from parent
  loading,
  isLoggedIn,
  currentUserGender,
  myProfile,
  devref,
  searchValues,
  searchIdValue,
}) {
  const navigate = useNavigate();

  // ✅ 1. Safe ID extraction (same as AllUsers.jsx)
  const getUserId = (item) => {
    return item.User_id || item.user_id || item.user || item.id || item.bid;
  };

  // ✅ 2. View profile navigation handler (same as AllUsers.jsx)
  const handleViewProfile = (userId) => {
    if (userId) {
      navigate(`/user-profile/${userId}`);
    } else {
      Swal.fire("Error", "Invalid user profile ID", "error");
    }
  };

  // ============================================
  // 🔴 STRICT CASTE FILTER logic
  // ============================================
  const getStrictCasteProfiles = () => {
    if (!isLoggedIn || !myProfile) return data;
    
    const myCaste = (myProfile.caste || "").toLowerCase().trim();
    if (!myCaste) {
      console.warn("⚠️ Login user has no caste configured.");
      return []; 
    }

    return data.filter((p) => {
      const pCaste = (p.caste || "").toLowerCase().trim();
      return pCaste === myCaste; // Sirf exact caste match profiles
    });
  };

  const filteredData = getStrictCasteProfiles();

  // ✅ Match type check (Both vs Caste)
  const getMatchType = (profile) => {
    if (!myProfile || !isLoggedIn) return null;
    const myCaste = (myProfile.caste || "").toLowerCase().trim();
    const myReligion = (myProfile.religion || "").toLowerCase().trim();
    const pCaste = (profile.caste || "").toLowerCase().trim();
    const pReligion = (profile.religion || "").toLowerCase().trim();

    const casteMatch = myCaste && pCaste === myCaste;
    const religionMatch = myReligion && pReligion === myReligion;

    if (casteMatch && religionMatch) return "both"; 
    if (casteMatch) return "caste"; 
    return null;
  };

  // ✅ Login prompt for non-logged users
  const handleLoginPrompt = () => {
    Swal.fire({
      title: "🔒 Login Required",
      html: `
        <div style="text-align: center; padding: 10px 0;">
          <p style="font-size: 15px; color: #666; margin: 0 0 8px;">
            You need to login or sign up to view all profiles
          </p>
          <p style="font-size: 13px; color: #999; margin: 0;">
            Join our community and find your perfect match! 💕
          </p>
        </div>
      `,
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#ff0044",
      cancelButtonColor: "#ff4d88",
      confirmButtonText: "🔐 Login",
      cancelButtonText: "✨ Sign Up",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) navigate("/User_Login");
      else if (result.dismiss === Swal.DismissReason.cancel) navigate("/User_Reg");
    });
  };

  const handleViewMore = () => {
    if (!isLoggedIn) {
      handleLoginPrompt();
      return;
    }
    navigate("/all-profiles");
  };

  // Pehli 4 profiles hi home page par show karenge (Strict filtered)
  const displayData = filteredData.slice(0, 4);

  return (
    <div className="res-section">
      {/* Header */}
      <div className="res-header">
        <span className="res-badge">
          <FaHeart className="res-badge-icon" />
          {filteredData.length > 0 ? "Your Matches" : "Discover Profiles"}
        </span>

        <h2 className="res-title">
          {filteredData.length > 0 ? (
            <>Found <span className="res-accent">{filteredData.length}</span> Perfect Matches</>
          ) : (
            <>Discover Your <span className="res-accent">Perfect Match</span></>
          )}
        </h2>

        <p className="res-sub">
          {filteredData.length > 0
            ? "Browse through verified profiles and find the one who completes you"
            : "Use the search above to find your soulmate today"}
        </p>

        {/* ✅ Strict Filter Info Badges */}
        {isLoggedIn && myProfile && filteredData.length > 0 && (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            flexWrap: "wrap",
            marginTop: 12,
          }}>
            <span style={{
              background: myProfile.gender === "Male"
                ? "linear-gradient(135deg, #eff6ff, #dbeafe)"
                : "linear-gradient(135deg, #fff0f3, #ffe4ec)",
              border: myProfile.gender === "Male"
                ? "1px solid #93c5fd" : "1px solid #fda4af",
              color: myProfile.gender === "Male" ? "#1e40af" : "#be123c",
              fontSize: 11, fontWeight: 700,
              padding: "5px 14px", borderRadius: 20,
            }}>
              {myProfile.gender === "Male" ? "👩 Female" : "👨 Male"} Profiles
            </span>

            {myProfile.caste && (
              <span style={{
                background: "linear-gradient(135deg, #fff0f3, #ffe4ec)",
                border: "1px solid #fda4af", color: "#ff0055",
                fontSize: 11, fontWeight: 700, padding: "5px 14px",
                borderRadius: 20, display: "flex", alignItems: "center", gap: 4,
              }}>
                <FaCrown style={{ fontSize: 10 }} /> Caste: {myProfile.caste} Only
              </span>
            )}

            {myProfile.religion && (
              <span style={{
                background: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
                border: "1px solid #c4b5fd", color: "#7c3aed",
                fontSize: 11, fontWeight: 700,
                padding: "5px 14px", borderRadius: 20,
              }}>
                🙏 {myProfile.religion}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Loading */}
      {loading ? (
        <div className="res-loading">
          <div className="res-loading-heart">💖</div>
          <Spin size="large" style={{ color: "#ff0055" }} />
          <p className="res-loading-text">Searching for your perfect match...</p>
        </div>
      ) : (
        <>
          <div className="res-grid">
            {displayData.length > 0 ? (
              displayData.map((item, index) => {
                const matchType = getMatchType(item);
                const userId = getUserId(item);

                return isLoggedIn ? (
                  // ✅ 1. Logged in user: Click karne par /user-profile/:id khulega
                  <div 
                    key={index} 
                    style={{ position: "relative", cursor: "pointer" }}
                    onClick={() => handleViewProfile(userId)}
                    title="Click to view full profile"
                  >
                    {/* Best Match Badge */}
                    {matchType === "both" && (
                      <div style={{
                        position: "absolute", top: 12, left: 12, zIndex: 10,
                        background: "linear-gradient(135deg, #ff0055, #ff4d88)",
                        color: "#fff", fontSize: 10, fontWeight: 800,
                        padding: "5px 14px", borderRadius: 20,
                        boxShadow: "0 4px 12px rgba(255,0,85,0.3)",
                        display: "flex", alignItems: "center", gap: 4,
                      }}>
                        <FaCrown style={{ fontSize: 11, color: '#ffd700' }} /> Best Match
                      </div>
                    )}

                    {/* Caste Match Badge */}
                    {matchType === "caste" && (
                      <div style={{
                        position: "absolute", top: 12, left: 12, zIndex: 10,
                        background: "linear-gradient(135deg, #f59e0b, #fbbf24)",
                        color: "#fff", fontSize: 10, fontWeight: 800,
                        padding: "5px 14px", borderRadius: 20,
                        boxShadow: "0 4px 12px rgba(245,158,11,0.3)",
                        display: "flex", alignItems: "center", gap: 4,
                      }}>
                        <FaStar style={{ fontSize: 10 }} /> Caste Match
                      </div>
                    )}

                    <ProfileCard
                      user={item}
                      baseurl={BASE_API}
                      style={{
                        border: matchType === "both" ? "2.5px solid #ff0055"
                          : matchType === "caste" ? "2px solid #f59e0b"
                          : undefined,
                        boxShadow: matchType === "both"
                          ? "0 6px 24px rgba(255,0,85,0.14)"
                          : matchType === "caste"
                          ? "0 6px 20px rgba(245,158,11,0.12)"
                          : undefined,
                      }}
                    />
                  </div>
                ) : (
                  // ✅ 2. Non-logged user: Click karne par Login popup aayega
                  <div 
                    key={index} 
                    style={{ cursor: "pointer" }}
                    onClick={handleLoginPrompt}
                  >
                    <LockedCard
                      user={item}
                      baseurl={BASE_API}
                      devref={devref}
                    />
                  </div>
                );
              })
            ) : (
              <div className="res-empty">
                <div className="res-empty-icon">💕</div>
                <h3 className="res-empty-title">No Matches Found Yet</h3>
                <p className="res-empty-desc">
                  {isLoggedIn && myProfile ? (
                    <>
                      No matching {myProfile.gender === "Male" ? "Female" : "Male"} profiles found with caste "{myProfile.caste || 'Not Configured'}".
                      <br />
                      <span style={{ fontSize: "13px", color: "#999" }}>
                        Try updating your profile preferences or checking back later.
                      </span>
                    </>
                  ) : (
                    "Try adjusting your search filters to discover more profiles"
                  )}
                </p>
                <div className="res-empty-chips">
                  <span>💍 15K+ Profiles</span>
                  <span>✅ Verified Members</span>
                  <span>🔒 100% Secure</span>
                </div>
              </div>
            )}
          </div>

          {filteredData.length > 4 && (
            <div className="view-more-wrapper">
              <button className="view-more-btn" onClick={handleViewMore}>
                <span>View All {filteredData.length} Profiles</span>
                <FaArrowRight className="view-more-arrow" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ResultsSection;